/** Xano JSON columns default to `{}` (not `null`/`[]`) when unset, so a line
 *  item's `included` / `not_included` can arrive as an empty OBJECT rather than
 *  an array. `?? []` doesn't catch that (it's not null), and downstream
 *  `.join`/`.filter`/`.map` then throw. Coerce anything non-array to `null` at
 *  the API seam so the runtime honours the declared `string[] | null` type and
 *  every consumer (editor + renderer) can treat these as arrays. */

import type { LineItem } from "@/types";

const asLines = (v: unknown): string[] | null => (Array.isArray(v) ? (v as string[]) : null);

/** Normalise the bullet-list JSON columns on one line item. */
export function normaliseLineItem(item: LineItem): LineItem {
  return { ...item, included: asLines(item.included), not_included: asLines(item.not_included) };
}

/** Normalise every line item on a quote-shaped object, leaving the rest intact.
 *  Accepts any object (incl. the public read's discriminated union, whose
 *  non-`live` members carry no `line_items`) — a no-op when absent. */
export function normaliseLineItems<T extends object>(quote: T): T {
  const items = (quote as { line_items?: unknown }).line_items;
  if (!Array.isArray(items)) return quote;
  return { ...quote, line_items: items.map(normaliseLineItem) };
}

const SHORT_DESCRIPTION_TOKEN = "{{short_description}}";

/** Lowercase the first letter — but leave an acronym-led value alone (so
 *  "VR experience" stays "VR experience", while "Ground floor extension"
 *  becomes "ground floor extension"). */
const lowerFirst = (s: string): string =>
  /^[A-Z][a-z]/.test(s) ? s[0].toLowerCase() + s.slice(1) : s;

/** Capitalise the first letter when it's lowercase (for sentence/title starts). */
const upperFirst = (s: string): string => (/^[a-z]/.test(s) ? s[0].toUpperCase() + s.slice(1) : s);

/** A token sits at a sentence start if only whitespace precedes it, or the text
 *  before it ends with sentence-ending punctuation. */
const atSentenceStart = (before: string): boolean => {
  const trimmed = before.replace(/\s+$/, "");
  return trimmed === "" || /[.!?:]$/.test(trimmed);
};

/** Template tokens in line-item text, resolved at RENDER time from the quote
 *  record (so a quote made from a template — where the project description is
 *  filled in only after creation — still fills in correctly, and stays in sync
 *  if it's edited later). Today the only token is `{{short_description}}` →
 *  the quote's project_description.
 *
 *  Casing is corrected per occurrence so the value reads naturally regardless
 *  of how it was typed: lowercased mid-sentence ("…approval of your ground
 *  floor extension") and capitalised at a sentence/title start — while an
 *  acronym-led value (e.g. "VR experience") is preserved.
 *
 *  An empty value leaves the token in place, so an unfilled quote shows the
 *  placeholder (a visible "fill me in" in the staff preview) rather than a
 *  dangling "…approval of your ." */
export function resolveTokens(
  text: string | null | undefined,
  shortDescription: string | null | undefined,
): string {
  const t = text ?? "";
  const sd = (shortDescription ?? "").trim();
  if (!sd || !t.includes(SHORT_DESCRIPTION_TOKEN)) return t;

  let out = "";
  let i = 0;
  for (;;) {
    const idx = t.indexOf(SHORT_DESCRIPTION_TOKEN, i);
    if (idx === -1) {
      out += t.slice(i);
      break;
    }
    out += t.slice(i, idx);
    out += atSentenceStart(out) ? upperFirst(sd) : lowerFirst(sd);
    i = idx + SHORT_DESCRIPTION_TOKEN.length;
  }
  return out;
}
